import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const HtmlDocList = () => {
  const [data, setData] = useState([]);
  const [campaignId, setCampaignId] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  const [activeStatus, setActiveStatus] = useState("");
  const [message, setMessage] = useState("");
  const [publishInfo, setPublishInfo] = useState({});
  const [htmlDocId, setHtmlDocId] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Copy dialog states
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [subusers, setSubusers] = useState([]);
  const [loadingSubusers, setLoadingSubusers] = useState(false);
  const [selectedSubuser, setSelectedSubuser] = useState("");
  const [selectedHtmlDocForCopy, setSelectedHtmlDocForCopy] = useState(null);
  const [messageType, setMessageType] = useState(""); // Track message type for styling

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  const fetchHtmlDocData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/Search',
        {
          id: 0,
          applycreateddate: false,
          fromcreateddate: from,
          endcreateddate: to,
          active: activeStatus,
          name: "",
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        const rows = response.data.data;
        setHasMore(rows.length === dataLimit);
        const approximateTotal = (count * dataLimit) + rows.length + (rows.length === dataLimit ? dataLimit : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching HTML document data:", error);
      setData([]);
      setHasMore(false);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchHtmlDocDataForId = async () => {
    try {
      const response = await axios.get('https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/GetDistinct/name', {
        headers: {
          accept: "text/plain",
          "X-Authorization": token,
          UserId: userid,
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        setHtmlDocId(response.data.data);
      } else {
        setHtmlDocId([]);
      }
    } catch (error) {
      console.error("Error fetching HTML document IDs:", error);
    }
  };

  useEffect(() => {
    fetchHtmlDocData();
    fetchHtmlDocDataForId();
  }, [getUrlByOperation, token, userid, dataLimit, count, from, to, activeStatus]);

  // Function to fetch subusers
  const fetchSubusers = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    setLoadingSubusers(true);
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserOperation/Search',
        {
          "id": 0,
          "applycreateddate": false,
          "applymodifieddate": false,
          "fromcreateddate": 0,
          "endcreateddate": 0,
          "frommodifieddate": 0,
          "endmodifieddate": 0,
          "active": "",
          "catalogname": "",
          "ispageload": false,
          "pagesize": 10,
          "currentpageno": 0,
          "withoutpagination": false,
          "pagedirection": "first"
        },
        {
          headers: {
            'X-Authorization': token,
            'UserId': userid,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data)) {
        setSubusers(response.data.data);
      } else {
        setSubusers([]);
      }
    } catch (error) {
      console.error("Error fetching subusers:", error);
      setSubusers([]);
    } finally {
      setLoadingSubusers(false);
    }
  };

  // Function to open copy dialog
  const handleOpenCopyDialog = (item) => {
    setSelectedHtmlDocForCopy(item);
    setSelectedSubuser("");
    setShowCopyDialog(true);
    fetchSubusers();
  };

  // Function to close copy dialog
  const handleCloseCopyDialog = () => {
    setShowCopyDialog(false);
    setSelectedHtmlDocForCopy(null);
    setSelectedSubuser("");
    setSubusers([]);
  };

  // Function to handle copy action
  const handleCopyAction = async () => {
    if (!selectedSubuser) {
      setMessage("Please select a subuser first");
      setMessageType("error");
      return;
    }
    
    if (!selectedHtmlDocForCopy) {
      setMessage("No HTML document selected");
      setMessageType("error");
      return;
    }

    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      setMessage("Authentication required");
      setMessageType("error");
      return;
    }

    try {
      console.log("Making copy API call with selected subuser:", selectedSubuser);
      console.log("Making copy API call with HTML document:", selectedHtmlDocForCopy);
      
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/Copy',
        {
          "subuserid": selectedSubuser, // Use selected subuser ID
          "copyid": selectedHtmlDocForCopy.id, // Use selected HTML document ID
          "name": selectedHtmlDocForCopy.name // Use selected HTML document name
        },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      console.log("Copy API response:", response.data);

      // Check different possible response structures
      if (response.data) {
        if (response.data.isresponse) {
          setMessage(`HTML document copied Successfully!`);
          setMessageType("success");
          handleCloseCopyDialog(); // Close dialog after successful copy
        } else if (response.data.responsestatus === "SUCCESS") {
          setMessage(`HTML document copied Successfully!`);
          setMessageType("success");
          handleCloseCopyDialog(); // Close dialog after successful copy
        } else {
          console.error("API response indicates failure:", response.data);
          const errorMessage = response.data.errormsg || response.data.message || 'Unknown error';
          setMessage(`Failed to copy HTML document: ${errorMessage}`);
          setMessageType("error");
        }
      } else {
        console.error("No response data received");
        setMessage("Failed to copy HTML document: No response from server");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error copying HTML document:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error status:", error.response.status);
        setMessage(`Failed to copy HTML document: ${error.response.data?.message || error.message}`);
        setMessageType("error");
      } else {
        setMessage(`Failed to copy HTML document: ${error.message}`);
        setMessageType("error");
      }
    }
  };

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddPop = () => {
    navigate("/addhtmldoc");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updatehtmldoc/${id}`);
  };

  const handleShowPop = (id, name) => {
    navigate(`/showhtmldoc/${id}/${name}`);
  };

  const handleDelete = async (id) => {
    try {
      await axios.post('https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/Delete', { id }, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setMessage("Data deleted successfully!");
      fetchHtmlDocData(); // Refresh data after delete
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
    }
  };

  const handleToggle = async (item) => {
    const apiUrl = item.active 
      ? 'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/UpdateStatus/Deactive'
      : 'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/UpdateStatus/Active';

    try {
      await axios.post(apiUrl, { id: item.id }, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
        setCopySuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

   
const handleCopyClick = async (id, publishId) => {
  const publishUrl = `${window.location.origin}/SMD/htmldoc/${publishId}`;
  
  try {
    await copyToClipboard(publishUrl);
    setMessage("URL copied to clipboard!");
    console.log("URL copied to clipboard:", publishUrl);
    setCopySuccess(true);
  } catch (error) {
    console.error("Failed to copy URL:", error);
    setMessage("Failed to copy URL to clipboard");
  }
};


// Updated handlePublish function with fixed clipboard handling
const handlePublish = async (id) => {
  setPublishLoading(true);
  try {
    // First, call the API to generate the publish ID
    const response = await axios.post(
      'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/Generate/PublishId',
      { id },
      {
        headers: {
          'accept': 'text/plain',
          'UserId': userid || '1',
          'X-Authorization': token,
          'Content-Type': 'application/json-patch+json'
        }
      }
    );

    // Check if the API response is successful
    if (response.data && response.data.isresponse) {
      // Get the publish ID from the response
      const publishId = response.data.publishid || "";
      
      // Generate the publishable URL
      const publishUrl = `${window.location.origin}/SMD/htmldoc/${publishId}`;
      
      // Update state with the publish info
      setPublishInfo({ 
        id, 
        encodedUrl: publishUrl, 
        encodedId: publishId 
      });
      
      // Copy URL to clipboard using your fallback function
      try {
        await copyToClipboard(publishUrl);
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      } catch (copyError) {
        console.error("Failed to copy URL:", copyError);
        setMessage("Publish successful, but failed to copy URL to clipboard");
      }
      
      // Refresh the data to get the updated publishid
      fetchHtmlDocData();
    } else {
      // Handle API error
      throw new Error(response.data?.errormsg || "Failed to generate publish ID");
    }
  } catch (error) {
    console.error("Error generating publish ID:", error);
    setMessage(error.message || "Failed to generate publish URL");
  } finally {
    setPublishLoading(false);
  }
};

// Ensure your copyToClipboard function is properly defined 
function copyToClipboard(text) {
  // Try to use the Clipboard API first
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(err => {
        console.error('Could not copy text using Clipboard API: ', err);
        return fallbackCopyToClipboard(text);
      });
  } else {
    // Fall back to the textarea method
    return Promise.resolve(fallbackCopyToClipboard(text));
  }
}

function fallbackCopyToClipboard(text) {
  // Create a temporary textarea element
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Make the textarea out of viewport
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  
  // Select and copy
  textArea.focus();
  textArea.select();
  
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (err) {
    console.error('Failed to copy with fallback method:', err);
  }
  
  // Remove the textarea from DOM
  document.body.removeChild(textArea);
  return success;
}

  const handleUnPublish = async (id, publishId) => {
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/HtmlDocOperation/UnPublishId',
        { id },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      if (response.data && response.data.isresponse) {
        setMessage("HTML Document unpublished successfully!");
        fetchHtmlDocData();
      } else {
        throw new Error(response.data?.errormsg || "Failed to unpublish HTML document");
      }
    } catch (error) {
      console.error("Error unpublishing HTML document:", error);
      setMessage(error.message || "Failed to unpublish HTML document");
    }
  };



  // Function to render the publish column
   const renderPublishColumn = (item) => {
    // If the item already has a publishid, show it with a copy button
    if (item.publishid) {
      // Truncate publishid if it's longer than 5 characters
      const displayId = item.publishid.length > 5 
        ? `${item.publishid.substring(0, 5)}...` 
        : item.publishid;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid)}
              title="Copy URL"
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
            >
              <i className="fas fa-eye-slash"></i>
            </button>
          </div>
        </div>
      );
    }
    
    // Check if this item is currently being processed
    if (publishInfo.id === item.id) {
      // Truncate encodedId if it's longer than 5 characters
      const displayId = publishInfo.encodedId.length > 5 
        ? `${publishInfo.encodedId.substring(0, 5)}...` 
        : publishInfo.encodedId;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleCopyClick(item.id, publishInfo.encodedId)}
            title="Copy URL"
          >
            <i className="fas fa-copy mr-1"></i> Copy URL
          </button>
        </div>
      );
    }
    
    // If no publishid and not currently processing, show the publish button
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => handlePublish(item.id)}
        disabled={publishLoading}
      >
        {publishLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-globe mr-1"></i> Publish
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">HTML Documents</h1>
                <button type="button" className="btn btn-success" onClick={handleAddPop}>
                  Add HTML Document
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                  <table className="table table-hover table-striped">
   <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
    <tr>
      <th style={{width: '5%'}} className="text-center">Id</th>
      <th style={{width: '15%'}}>Name</th>
      <th style={{width: '15%'}}>HTML Document Description</th>
      <th style={{width: '15%'}} className="text-center">Show Content</th>
      <th style={{width: '25%'}} className="text-center">Action</th>
      <th style={{width: '10%'}} className="text-center">Active</th>
      <th style={{width: '15%'}} className="text-center">Publish</th>
    </tr>
  </thead>
  <tbody>
    {data.map((item, index) => (
      <tr key={index}>
        <td className="text-center">{item.id}</td>
        <td>
          <strong>{item.name}</strong>
        </td>
        <td>{item.remark || "-"}</td>
        <td className="text-center">
          <button
            type="button"
            className="btn btn-sm btn-info"
            onClick={() => handleShowPop(item.id, item.name)}
          >
            <i className="fas fa-file-code mr-1"></i>
          </button>
        </td>
        <td className="text-center">
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-sm btn-info mr-1"
              onClick={() => handleShowPop(item.id, item.name)}
            >
              <i className="fas fa-file-code mr-1"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-warning mr-1"
              onClick={() => handleUpdatePop(item.id)}
            >
              <i className="fas fa-edit mr-1"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger mr-1"
              onClick={() => handleDelete(item.id)}
            >
              <i className="fas fa-trash mr-1"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-success mr-1"
              onClick={() => handleOpenCopyDialog(item)}
            >
              <i className="fas fa-copy mr-1"></i>
            </button>
          </div>
        </td>
        <td className="text-center">
          <Switch
            isOn={item.active}
            handleToggle={() => handleToggle(item)}
            id={item.id}
          />
        </td>
        <td className="text-center">
          {renderPublishColumn(item)}
        </td>
      </tr>
    ))}
  </tbody>
</table>
                    </div>

                    <Pagination
                      currentPage={count + 1}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={totalItems}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display - show both as toast notification AND component */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${copySuccess || message === "Data deleted successfully!" || messageType === "success" ? "alert-success" : "alert-danger"} d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}
      
      {message === "URL copied to clipboard!" && <SuccessMessage message={message} />}
      {message === "Data deleted successfully!" && <SuccessMessage message={message} />}
      {message === "Failed to delete data" && <FailedMessage message={message} />}
      
      {/* Copy Dialog */}
      {showCopyDialog && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "block"
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document" style={{ width: "100%", maxWidth: "400px" }}>
            <div className="modal-content" style={{ borderRadius: "15px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #f0f0f0", padding: "1.5rem" }}>
                <h5 className="modal-title" style={{ fontWeight: "700", color: "#333", fontSize: "1.25rem" }}>
                  <i className="fas fa-copy mr-2 text-success"></i>
                  Copy HTML Document to Subuser
                </h5>
                <button type="button" className="close" onClick={handleCloseCopyDialog} style={{ outline: "none" }}>
                  <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: "1.5rem" }}>
                {message && (
                  <div className="alert alert-info" role="alert">
                    {message}
                  </div>
                )}

                <div className="form-group">
                  <label style={{ fontWeight: "600", color: "#495057", marginBottom: "0.5rem", display: "block" }}>
                    <i className="fas fa-user mr-2 text-muted"></i>Select Subuser
                  </label>
                  <select
                    className="form-control custom-select"
                    value={selectedSubuser}
                    onChange={(e) => setSelectedSubuser(e.target.value)}
                    style={{ borderRadius: "8px", padding: "0.6rem 1rem", height: "auto" }}
                    disabled={loadingSubusers}
                  >
                    <option value="">{loadingSubusers ? "Loading subusers..." : "Select subuser..."}</option>
                    {subusers.map((subuser, index) => (
                      <option key={index} value={subuser.id}>
                        {subuser.username}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedHtmlDocForCopy && (
                  <div className="mt-3 p-3 bg-light rounded" style={{ fontSize: "0.85rem" }}>
                    <strong>Selected HTML Document:</strong> {selectedHtmlDocForCopy.name}
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #f0f0f0", padding: "1.25rem 1.5rem", justifyContent: "space-between" }}>
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseCopyDialog}
                  style={{ fontWeight: "600", color: "#6c757d", padding: "0.6rem 1.5rem" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCopyAction}
                  disabled={loadingSubusers || !selectedSubuser}
                  style={{
                    fontWeight: "700",
                    padding: "0.6rem 2rem",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(40,167,69,0.3)",
                    transition: "transform 0.2s"
                  }}
                >
                  <i className="fas fa-copy mr-2"></i>
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HtmlDocList;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}